import axios, { AxiosRequestHeaders, CanceledError } from 'axios';
import { API_BASE_URL, API_V1_URL, JWT_APP_V1 } from '@/config/api.config';

// Permet à une requête de s'exécuter sans authentification (ex. vérification de
// version au démarrage) : un 401 ne doit alors pas déclencher de déconnexion
// forcée ni de redirection vers l'écran de connexion.
declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuthInterceptor?: boolean;
  }
}
import { Patient } from '@/types/patient';
import { Preferences } from '@capacitor/preferences';
import { refreshToken, logout, checkHasRefreshToken } from '@/services/auth';
import router from '@/router';
import { LINKS } from '@/utils/links';
import * as Sentry from '@sentry/capacitor';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'x-env': 'mobile',
  },
});

export const api_v1 = axios.create({
  baseURL: API_V1_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    authorization: `JWT ${JWT_APP_V1}`,
  },
});

const attachJwtInterceptor = (instance: typeof api) => {
  instance.interceptors.request.use(
    async (config) => {
      const { value: jwt } = await Preferences.get({ key: 'jwt' });

      if (jwt) {
        config.headers = {
          ...config.headers,
          Authorization: `JWT ${jwt}`,
        } as AxiosRequestHeaders;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );
};

const forceLogout = async () => {
  await logout();
  await router.replace(LINKS.AUTH.SIGN_IN);
};

const isRefreshRequest = (url?: string) => url?.includes('/auth/refresh-token') ?? false;

let refreshPromise: Promise<unknown> | null = null;

const attach401Interceptor = (instance: typeof api) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Requête explicitement non authentifiée : on remonte l'erreur sans
      // forcer la déconnexion ni rediriger.
      if (originalRequest?.skipAuthInterceptor) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        const hasRefreshToken = await checkHasRefreshToken();
        if (isRefreshRequest(originalRequest.url) || !hasRefreshToken) {
          refreshPromise = null;
          await forceLogout();
          throw new CanceledError('Refresh token non disponible.');
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;

          try {
            if (!refreshPromise) {
              refreshPromise = refreshToken().finally(() => {
                refreshPromise = null;
              });
            }
            await refreshPromise;

            const { value: jwt } = await Preferences.get({ key: 'jwt' });
            originalRequest.headers.Authorization = `JWT ${jwt}`;
            return instance(originalRequest);
          } catch {
            await forceLogout();
            return Promise.reject(error);
          }
        }

        await forceLogout();
      }

      Sentry.captureException(error);
      return Promise.reject(error);
    },
  );
};

attachJwtInterceptor(api);
attachJwtInterceptor(api_v1);
attach401Interceptor(api);
attach401Interceptor(api_v1);

export const fetchPatients = async (): Promise<Patient[]> => {
  const response = await api_v1.get<Patient[]>('/pros/account/patients');
  return response.data;
};
